import { useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import {
  AlertIcon,
  ArrowIcon,
  AuthSplitShell,
} from '../components/layout/AuthSplitShell.js';
import { client } from '../main.js';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const errorRef = useRef<HTMLDivElement>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!token) return <Navigate to="/forgot-password" replace />;

  const formValid = password.length >= 8 && confirm.length > 0;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    setLoading(true);
    try {
      await client.auth.resetPassword({ token: token!, password });
      navigate('/login', {
        replace: true,
        state: { flash: 'Password updated — please sign in.' },
      });
    } catch (err) {
      setError(
        (err as Error).message || 'Reset failed — the link may have expired.',
      );
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setLoading(false);
    }
  }

  const eyeButton = (onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={show ? 'Hide password' : 'Show password'}
      tabIndex={-1}
      style={{
        position: 'absolute',
        right: 14,
        top: '50%',
        transform: 'translateY(-50%)',
        border: 0,
        background: 'none',
        cursor: 'pointer',
        color: '#9a9ab4',
        display: 'flex',
      }}
    >
      {show ? <EyeOff size={17} /> : <Eye size={17} />}
    </button>
  );

  return (
    <AuthSplitShell
      eyebrow="Password reset"
      headline={
        <>
          Almost there.
          <br />
          <em>Pick a new one.</em>
        </>
      }
      description={
        <>
          <strong>At least eight characters.</strong> Once it’s saved you’ll
          sign in with the new password.
        </>
      }
    >
      <form className="auth-form" noValidate onSubmit={(e) => void onSubmit(e)}>
        <h1>New password</h1>
        <p className="auth-subtitle">Choose a password you don’t use elsewhere.</p>

        {error && (
          <div ref={errorRef} className="auth-banner" role="alert" tabIndex={-1}>
            <AlertIcon />
            <p>{error}</p>
          </div>
        )}

        <label htmlFor="password">New password</label>
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <input
            id="password"
            name="password"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="••••••••"
            className="auth-password-input"
            value={password}
            disabled={loading}
            autoFocus
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
          />
          {eyeButton(() => setShow((v) => !v))}
        </div>

        <label htmlFor="confirm">Confirm password</label>
        <div style={{ position: 'relative' }}>
          <input
            id="confirm"
            name="confirm"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="••••••••"
            className="auth-password-input"
            value={confirm}
            disabled={loading}
            onChange={(e) => {
              setConfirm(e.target.value);
              setError(null);
            }}
          />
          {eyeButton(() => setShow((v) => !v))}
        </div>

        <button
          type="submit"
          className="auth-primary-button auth-login-button"
          disabled={!formValid || loading}
          aria-busy={loading}
        >
          {loading ? (
            <>
              <span className="auth-spinner" aria-hidden="true" /> Saving
            </>
          ) : (
            <>
              Set new password <ArrowIcon />
            </>
          )}
        </button>

        <div className="auth-alt-action" style={{ justifyContent: 'center' }}>
          <Link to="/login">Back to sign in</Link>
        </div>
      </form>
    </AuthSplitShell>
  );
}
